// Secondary parking layer. Rendered imperatively (Leaflet, no React re-renders):
// individual icons when there are only a few points or when zoomed in, subtle
// clusters only for genuinely dense areas, and restrictions drawn as soft area
// overlays where the source geometry supports it.

import React from "react";
import { toast } from "sonner";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { fetchParkingPlaces, fetchStarterParking, thinBySpacing } from "../lib/parkingPlaces";
import { KIND_META, CONFIDENCE_META, RESTRICTION_KINDS, accessibleLabel, accessibleSource } from "../lib/mapLayers";
import { sendPlaceFeedback, lastCheckedLabel, myVote, rememberVote, votePosition } from "../lib/community";
import { placeIcon, clusterIcon } from "../lib/placeIcons";
import { track } from "../lib/analytics";

const STARTER_UNTIL_ZOOM = 13;  // below this: one representative marker per screen cell
const LOW_ZOOM_CELL_PX = 120;   // screen grid used for that thinning
const LOW_ZOOM_MAX = 10;        // 6-10 useful elements in a wide view
const STARTER_FALLBACK_ZOOM = 14; // widen to this once if the first view is empty
const NO_CLUSTER_ZOOM = 16;     // individual icons from here up
const CLUSTER_MIN_POINTS = 8;   // below this, never cluster
const DIM_OPACITY = 0.4;        // secondary parking while a destination is chosen
const STREET_LINE_MIN_ZOOM = 15; // street sections only appear close in
const ACCESSIBLE_BAY_MIN_ZOOM = 14; // single bays are tiny: avoid pin soup
const THROTTLE_MS = 350;

const esc = (s) =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// Popups must stay inside a phone screen: pan clear of the top bars and of the
// bottom action row instead of being drawn half off-screen.
const POPUP_OPTS = {
  closeButton: true,
  autoPan: true,
  autoPanPaddingTopLeft: [14, 104],
  autoPanPaddingBottomRight: [14, 120],
  maxHeight: 320,
  keepInView: true,
};

function popupHtml(p) {
  const meta = KIND_META[p.kind] || KIND_META.car_park;
  const conf = CONFIDENCE_META[p.confidence] || CONFIDENCE_META.imported;
  const restriction = RESTRICTION_KINDS.includes(p.kind);
  const voted = myVote(p.id);
  const rows = [];
  if (p.rule?.label) rows.push(esc(p.rule.label));
  else {
    if (p.fee === true) rows.push("Paid");
    if (p.fee === false) rows.push("Free");
  }
  if (p.capacity) rows.push(`${p.capacity} spaces`);
  if (p.schedule && !p.rule?.label) rows.push(esc(p.schedule));
  const acc = accessibleLabel(p.accessible);
  const accRow = acc
    ? `<div style="margin-top:6px;font:600 11px 'Manrope',sans-serif;color:#93c5fd">♿ ${esc(acc)} · ${esc(
        accessibleSource(p.accessible)
      )}</div>` +
      (p.accessible.access_note
        ? `<div style="margin-top:4px;font:500 11px 'Manrope',sans-serif;color:#fbbf24">${esc(
            p.accessible.access_note
          )}</div>`
        : "")
    : `<div style="margin-top:6px;font:500 11px 'Manrope',sans-serif;color:#71717a">Accessible bays not known</div>`;
  const dir = `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`;
  return `
    <div style="min-width:172px">
      <div style="font:700 13px 'Manrope',sans-serif;color:#fff">${esc(p.name || meta.label)}</div>
      <div style="margin-top:3px;font:600 9px 'Manrope',sans-serif;letter-spacing:0.08em;color:${conf.color}">${conf.label}</div>
      ${p.needs_check ? `<div style="margin-top:6px;padding:6px 8px;border-radius:8px;background:rgba(245,158,11,.12);border:1px solid rgba(245,158,11,.35);font:600 11px 'Manrope',sans-serif;color:#fcd34d;line-height:1.35">Reported by a driver — not parking advice yet. Are you here? Check the signs and confirm.</div>` : ""}
      ${rows.length ? `<div style="margin-top:6px;font:500 12px 'Manrope',sans-serif;color:#d4d4d8">${rows.join(" · ")}</div>` : ""}
      ${restriction ? "" : accRow}
      <div style="margin-top:6px;font:500 11px 'Manrope',sans-serif;color:#a1a1aa">${
        restriction ? "Parking restriction — check the signs on site." : "Parking option — spaces are not guaranteed."
      }</div>
      ${restriction ? "" : `<a href="${dir}" target="_blank" rel="noreferrer" style="margin-top:8px;display:inline-block;padding:6px 10px;border-radius:999px;background:#ccff00;color:#0a0a0c;font:700 12px 'Manrope',sans-serif;text-decoration:none">Directions</a>`}
      <div style="margin-top:8px;font:500 10px 'Manrope',sans-serif;color:#a1a1aa">${esc(
        p.source_label || "Not verified"
      )} · ${esc(lastCheckedLabel(p.community))}</div>
      ${p.provisional ? `<div style="margin-top:4px;font:600 10px 'Manrope',sans-serif;color:#fbbf24">Shown because drivers confirmed it — not officially verified</div>` : ""}
      <div style="margin-top:6px;display:flex;gap:6px">
        <button type="button" data-act="confirm" data-place="${esc(p.id)}" style="flex:1;padding:7px 8px;border-radius:999px;border:1px solid rgba(204,255,0,.45);background:${
          voted === "confirm" ? "rgba(204,255,0,.18)" : "transparent"
        };color:#ccff00;font:700 11px 'Manrope',sans-serif;cursor:pointer">${
          voted === "confirm" ? "✓ Still correct" : "Still correct"
        }</button>
        <button type="button" data-act="dispute" data-place="${esc(p.id)}" style="flex:1;padding:7px 8px;border-radius:999px;border:1px solid rgba(255,255,255,.18);background:${
          voted === "dispute" ? "rgba(255,255,255,.12)" : "transparent"
        };color:#e4e4e7;font:600 11px 'Manrope',sans-serif;cursor:pointer">${
          voted === "dispute" ? "✓ Reported" : "Not correct"
        }</button>
      </div>
      ${p.community?.disputes ? `<div style="margin-top:5px;font:500 10px 'Manrope',sans-serif;color:#fbbf24">${p.community.confirms} confirmed · ${p.community.disputes} disputed by drivers</div>` : ""}
      ${p.source === "osm" ? `<div style="margin-top:6px;font:500 10px 'Manrope',sans-serif;color:#71717a">Source: OpenStreetMap</div>` : ""}
    </div>`;
}

function trackClick(p) {
  const event = RESTRICTION_KINDS.includes(p.kind)
    ? "restriction_marker_clicked"
    : p.kind === "free_parking"
    ? "free_parking_marker_clicked"
    : "car_park_marker_clicked";
  track(event, { kind: p.kind, confidence: p.confidence });
}

export default function ParkingPlacesLayer({ kinds, dim, focusPlaces, includeAccessible, includePendingCommunity }) {
  const map = useMap();
  const clusterRef = React.useRef(null);
  const plainRef = React.useRef(null);
  const areaRef = React.useRef(null);
  const widenedRef = React.useRef(false);
  const timerRef = React.useRef(null);
  const popupOpenRef = React.useRef(false);
  const kindsKey = kinds.join(",");
  const focusList = focusPlaces || [];
  const focusKey = focusList.map((p) => `${p.id}${p.selected ? "*" : ""}`).join(",");
  const scale = dim ? 0.86 : 1;

  React.useEffect(() => {
    const cluster = L.markerClusterGroup({
      chunkedLoading: true,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      disableClusteringAtZoom: NO_CLUSTER_ZOOM,
      maxClusterRadius: (zoom) => (zoom >= 15 ? 22 : zoom >= 14 ? 45 : 80),
      iconCreateFunction: (c) => clusterIcon(c.getChildCount(), scale),
    });
    const plain = L.layerGroup();
    const areas = L.layerGroup();
    clusterRef.current = cluster;
    plainRef.current = plain;
    areaRef.current = areas;
    areas.addTo(map);
    plain.addTo(map);
    map.addLayer(cluster);
    return () => {
      map.removeLayer(cluster);
      map.removeLayer(plain);
      map.removeLayer(areas);
      clusterRef.current = plainRef.current = areaRef.current = null;
    };
  }, [map, scale]);

  React.useEffect(() => {
    const cluster = clusterRef.current;
    const plain = plainRef.current;
    const areas = areaRef.current;
    if (!cluster || !plain || !areas) return;
    const activeKinds = kindsKey ? kindsKey.split(",") : [];
    const focus = new Set(focusList.map((p) => p.id));

    const clearAll = () => {
      cluster.clearLayers();
      plain.clearLayers();
      areas.clearLayers();
    };

    // One marker factory for every branch: the destination's own options stay
    // fully opaque, everything else fades back while a destination is chosen.
    const makeMarker = (p) => {
      // A place with dedicated Blue Badge bays carries the accessible glyph;
      // step-free access alone never changes the marker.
      // A report still awaiting confirmation never wears a parking glyph.
      const iconKind = p.needs_check
        ? "needs_check"
        : p.accessible?.has_bays ? "accessible_bays" : p.kind;
      const m = L.marker([p.lat, p.lng], { icon: placeIcon(iconKind, scale), keyboard: false });
      m.bindPopup(popupHtml(p), POPUP_OPTS);
      m.on("click", () => trackClick(p));
      if (focus.size) m.setOpacity(focus.has(p.id) ? 1 : DIM_OPACITY);
      return m;
    };

    // Recommended options whose layer is switched off still need a marker.
    const addMissingFocus = (renderedIds) => {
      focusList
        .filter((p) => !renderedIds.has(p.id) && Number.isFinite(p.lat)
          && p.geometry?.type !== "LineString")
        .forEach((p) => makeMarker(p).addTo(plain));
      // Street sections are drawn as a discreet line, never as a new pin layer:
      // only the ones belonging to the active journey ever appear.
      focusList
        .filter((p) => p.geometry?.type === "LineString" && map.getZoom() >= STREET_LINE_MIN_ZOOM)
        .forEach((p) => {
          L.geoJSON(p.geometry, {
            style: {
              color: "#ccff00",
              weight: p.selected ? 3 : 2,
              opacity: p.selected ? 0.6 : 0.35,
              lineCap: "round",
              interactive: false,
            },
          }).addTo(areas);
        });
    };

    const load = async () => {
      // An open popup lives inside the marker layer: its own autoPan fires
      // moveend, and reloading here would clear the layer and kill the popup
      // mid-tap. Reload once it closes instead.
      if (popupOpenRef.current) return;
      if (!activeKinds.length && !focusList.length && !includeAccessible && !includePendingCommunity) {
        clearAll();
        return;
      }
      const zoom = map.getZoom();
      const center = map.getCenter();
      // Single Blue Badge bays are tiny: keep them out of wide views entirely.
      const visible = (list) =>
        zoom >= ACCESSIBLE_BAY_MIN_ZOOM ? list : list.filter((p) => p.kind !== "accessible_bay");

      // Wide zoom: one representative marker per screen cell (6-10 in view), taken
      // from the same bbox response the higher zooms use — no extra backend call.
      if (zoom < STARTER_UNTIL_ZOOM) {
        const places = activeKinds.length || includeAccessible || includePendingCommunity
        ? await fetchParkingPlaces(map.getBounds(), activeKinds, { includeAccessible, includePendingCommunity })
        : [];
        clearAll();
        const spread = thinBySpacing(visible(places), map, LOW_ZOOM_CELL_PX, LOW_ZOOM_MAX);
        spread.forEach((p) => makeMarker(p).addTo(plain));
        addMissingFocus(new Set(spread.map((p) => p.id)));
        return;
      }

      const places = activeKinds.length || includeAccessible || includePendingCommunity
        ? visible(await fetchParkingPlaces(map.getBounds(), activeKinds, { includeAccessible, includePendingCommunity }))
        : [];

      // First load with nothing in view? Widen slightly (keeping the centre) so the
      // driver immediately sees that there are options around them.
      if (!places.length && !widenedRef.current && zoom > STARTER_FALLBACK_ZOOM) {
        widenedRef.current = true;
        const starter = await fetchStarterParking({
          lat: center.lat,
          lng: center.lng,
          radius_m: 2500,
          kinds: activeKinds,
          limit: 3,
        });
        if (starter.length) {
          map.setZoom(STARTER_FALLBACK_ZOOM);
          return;
        }
      }
      clearAll();

      const points = [];
      places.forEach((p) => {
        // Restrictions with real geometry become a soft area outline instead of a pin.
        if (RESTRICTION_KINDS.includes(p.kind) && p.geometry) {
          const coords =
            p.geometry.type === "Polygon" ? p.geometry.coordinates[0] : p.geometry.coordinates;
          const latlngs = coords.map(([lng, lat]) => [lat, lng]);
          const shape =
            p.geometry.type === "Polygon"
              ? L.polygon(latlngs, {
                  color: "#ef4444", weight: 1.4, opacity: 0.55,
                  fillColor: "#ef4444", fillOpacity: 0.12, interactive: true,
                })
              : L.polyline(latlngs, { color: "#ef4444", weight: 3, opacity: 0.5, interactive: true });
          shape.bindPopup(popupHtml(p), POPUP_OPTS);
          shape.on("click", () => trackClick(p));
          shape.addTo(areas);
          return;
        }
        points.push(p);
      });

      // Options that belong to the chosen destination are never clustered away.
      const markers = [];
      points.forEach((p) => {
        const m = makeMarker(p);
        if (focus.has(p.id)) m.addTo(plain);
        else markers.push(m);
      });

      // Only a handful of points, or zoomed in? Show them individually.
      const useCluster = markers.length >= CLUSTER_MIN_POINTS && map.getZoom() < NO_CLUSTER_ZOOM;
      if (useCluster) cluster.addLayers(markers);
      else markers.forEach((m) => m.addTo(plain));
      addMissingFocus(new Set(points.map((p) => p.id)));
    };

    const throttled = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(load, THROTTLE_MS);
    };

    load();
    const onPopupOpen = () => { popupOpenRef.current = true; };
    const onPopupClose = () => { popupOpenRef.current = false; throttled(); };
    map.on("popupopen", onPopupOpen);
    map.on("popupclose", onPopupClose);
    map.on("moveend", throttled);
    map.on("zoomend", throttled);
    return () => {
      map.off("popupopen", onPopupOpen);
      map.off("popupclose", onPopupClose);
      map.off("moveend", throttled);
      map.off("zoomend", throttled);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [map, kindsKey, focusKey, scale, includeAccessible, includePendingCommunity]);

  // One delegated handler for the "Still correct / Not correct" buttons in any
  // popup: the popups are plain HTML, so this avoids one listener per marker.
  React.useEffect(() => {
    if (!map) return;
    const onClick = async (e) => {
      const btn = e.target.closest?.("[data-act][data-place]");
      if (!btn) return;
      const placeId = btn.getAttribute("data-place");
      const vote = btn.getAttribute("data-act");
      btn.disabled = true;
      try {
        const res = await sendPlaceFeedback(placeId, vote, await votePosition());
        rememberVote(placeId, vote);
        btn.textContent = vote === "confirm" ? "✓ Thanks" : "✓ Reported";
        toast.success(
          vote === "confirm"
            ? "Thanks — marked as checked just now."
            : "Thanks — flagged for review."
        );
        if (res?.community?.disputes >= 3) {
          toast.message("Several drivers disagree here — we'll review it.");
        }
      } catch (err) {
        btn.disabled = false;
        toast.error(err?.response?.data?.detail || "Could not send that.");
      }
    };
    const el = map.getContainer();
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [map]);

  return null;
}
